import { useState } from 'react';
import { aboutData, siteConfig } from '@/mocks/siteData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }
    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'company_alt') params.append(key, value as string);
      });
      const res = await fetch('https://readdy.ai/api/form/d9tvvrf8l97n3eas3emg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const text = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMsg('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        setFormStatus('error');
        setFormMsg(parsed?.meta?.message || '提交失败，请稍后再试。');
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后重试。');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    description: siteConfig.description,
    url: 'https://axpar.cn',
    email: aboutData.contact.email,
    address: {
      '@type': 'PostalAddress',
      addressLocality: '上海',
      addressRegion: '浦东新区',
      addressCountry: 'CN',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://axpar.cn/' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      { '@type': 'Question', name: '亚洲视频精选平台是什么？', acceptedAnswer: { '@type': 'Answer', text: '亚洲视频精选平台是一个专注于亚洲内容的视频聚合网站，汇集来自日韩、东南亚、华语地区的精品视频资源。' } },
      { '@type': 'Question', name: '如何联系我们？', acceptedAnswer: { '@type': 'Answer', text: '您可以通过本页面的联系表单给我们留言，也可以发送邮件至 contact@axpar.cn。' } },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
        </div>

        <div className="w-full max-w-[800px] mx-auto px-4 md:px-6 lg:px-8 py-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-2">关于我们</h1>
          <p className="text-sm text-foreground-500 mb-12">了解亚洲视频精选平台的故事与使命</p>

          <section className="mb-14" aria-labelledby="story-title">
            <h2 id="story-title" className="font-heading text-2xl font-bold text-foreground-900 mb-6">
              {aboutData.brandStory.title}
            </h2>
            {aboutData.brandStory.paragraphs.map((p, idx) => (
              <p key={idx} className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">{p}</p>
            ))}
          </section>

          <section className="mb-14" aria-labelledby="stats-title">
            <h2 id="stats-title" className="font-heading text-2xl font-bold text-foreground-900 mb-6">平台数据</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {aboutData.reputation.map((item) => (
                <div key={item.label} className="bg-background-100 rounded-xl p-6 text-center">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-primary-600 mb-1">{item.metric}</p>
                  <p className="text-sm text-foreground-500">{item.label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-14" aria-labelledby="team-title">
            <h2 id="team-title" className="font-heading text-2xl font-bold text-foreground-900 mb-6">核心团队</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {aboutData.team.map((member) => (
                <div key={member.name} className="bg-white rounded-xl border border-background-200 p-6 text-center">
                  <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mx-auto mb-4">
                    <i className="ri-user-3-fill text-2xl text-primary-600"></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 mb-1">{member.name}</h3>
                  <p className="text-xs text-primary-600 font-medium mb-2">{member.role}</p>
                  <p className="text-xs text-foreground-500 leading-relaxed">{member.bio}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-10" aria-labelledby="contact-title">
            <h2 id="contact-title" className="font-heading text-2xl font-bold text-foreground-900 mb-6">联系我们</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">联系方式</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">邮箱</p>
                      <p className="text-sm text-foreground-800">{aboutData.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">地址</p>
                      <p className="text-sm text-foreground-800">{aboutData.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center flex-shrink-0">
                      <i className="ri-wechat-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500">微信公众号</p>
                      <p className="text-sm text-foreground-800">{aboutData.contact.wechat}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-sm font-semibold text-foreground-900 mb-4">给我们留言</h3>
                <form onSubmit={handleSubmit} data-readdy-form="">
                  <div className="form-honeypot">
                    <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <div className="space-y-3">
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="请输入留言内容（最多500字）"
                      required
                      className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    ></textarea>
                    <button
                      type="submit"
                      className="whitespace-nowrap w-full px-6 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                    >
                      发送留言
                    </button>
                    {formStatus !== 'idle' && (
                      <p className={`text-sm ${formStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                        {formMsg}
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}